import type { PageSetup } from '../model';

// #772 -- the drawing's page (docs/model/drawing-ui-viewport.md). Sizes are drawing units: 1 unit
// is 1 CSS px at 100% zoom, 96 per inch (accepted). Orientation is not stored: it follows width
// versus height, so turning the page swaps them.
export const UNITS_PER_INCH = 96;
export const UNITS_PER_MM = UNITS_PER_INCH / 25.4;
export const UNITS_PER_POINT = UNITS_PER_INCH / 72;
/** #859 -- a length in points (1 pt = 1/72 in) as drawing units. */
export const pointsToUnits = (points: number) => points * UNITS_PER_POINT;

export type PagePreset = PageSetup['preset'];
export type PageUnit = PageSetup['unit'];

// Portrait sizes. Letter 8.5 x 11 in; A4 210 x 297 mm; the 16:9 slide is 13.333 x 7.5 in
// (PowerPoint widescreen, accepted October 2) and is landscape by nature.
export const PAGE_PRESETS: Readonly<
  Record<Exclude<PagePreset, 'custom'>, { width: number; height: number; unit: PageUnit }>
> = {
  letter: { width: 8.5 * UNITS_PER_INCH, height: 11 * UNITS_PER_INCH, unit: 'in' },
  a4: { width: 210 * UNITS_PER_MM, height: 297 * UNITS_PER_MM, unit: 'mm' },
  'slide-16-9': { width: 13.333 * UNITS_PER_INCH, height: 7.5 * UNITS_PER_INCH, unit: 'in' }
};

// Grid spacing defaults to 5 mm.
export const DEFAULT_GRID_SPACING = 5 * UNITS_PER_MM;
export const DEFAULT_PAGE: PageSetup = { preset: 'letter', ...PAGE_PRESETS.letter };
export const A4_PAGE: PageSetup = { preset: 'a4', ...PAGE_PRESETS.a4 };

// Regions whose everyday paper is US Letter (the Americas' Letter countries and the Philippines);
// everywhere else uses A4.
const LETTER_REGIONS = new Set([
  'US',
  'CA',
  'MX',
  'PH',
  'CL',
  'CO',
  'VE',
  'PR',
  'CR',
  'GT',
  'PA',
  'DO',
  'SV',
  'NI',
  'HN',
  'BZ'
]);

// Owner decision (October 5): the default page follows the browser's region -- portrait Letter
// (inches) in Letter regions, portrait A4 (mm) elsewhere. A language with no region is expanded
// to its likely one ("en" is US). Unknown or unparsable locales fall back to Letter.
export function regionalDefaultPage(locale?: string): PageSetup {
  const tag = locale ?? (typeof navigator === 'undefined' ? undefined : navigator.language);
  if (!tag) return DEFAULT_PAGE;
  try {
    const region = new Intl.Locale(tag).maximize().region;
    return !region || LETTER_REGIONS.has(region) ? DEFAULT_PAGE : A4_PAGE;
  } catch {
    return DEFAULT_PAGE;
  }
}

/**
 * ADR 0025 D7 (#1143, owner): the page a saved drawing keeps -- only one its author chose. A page
 * the same as the regional default (size, orientation, unit, grid) was not a choice, so it isn't
 * recorded, and the drawing opens on each reader's own default. Its extent is derived from the
 * content on read (#805), so it is never saved either.
 */
export function chosenPage(page: PageSetup | undefined, locale?: string): PageSetup | undefined {
  if (!page) return undefined;
  const { extent: _extent, ...base } = page;
  const regional = regionalDefaultPage(locale);
  const same =
    base.preset === regional.preset &&
    base.width === regional.width &&
    base.height === regional.height &&
    base.unit === regional.unit &&
    gridSpacingOf(base) === gridSpacingOf(regional);
  return same ? undefined : base;
}

// A new drawing is stamped with its page when created, so it keeps it wherever it is opened. One
// with no page (saved before #772) shows the viewer's regional default and keeps every position.
export function pageOf(document: { readonly page?: PageSetup }): PageSetup {
  return document.page ?? regionalDefaultPage();
}

export const gridSpacingOf = (page: PageSetup) => page.gridSpacing ?? DEFAULT_GRID_SPACING;
export const isLandscape = (page: PageSetup) => page.width > page.height;

// Turning the page swaps its sides; content positions are untouched (accepted: orientation
// changes preserve artwork positions and sizes).
export function withOrientation(page: PageSetup, landscape: boolean): PageSetup {
  if (isLandscape(page) === landscape) return page;
  return { ...page, width: page.height, height: page.width };
}

// A preset in the current orientation, keeping the user's unit choice and grid spacing.
export function withPreset(page: PageSetup, preset: Exclude<PagePreset, 'custom'>): PageSetup {
  const size = PAGE_PRESETS[preset];
  const landscape = preset === 'slide-16-9' ? true : isLandscape(page);
  const sized = {
    ...page,
    preset,
    width: size.width,
    height: size.height,
    unit: page.preset === 'custom' ? page.unit : size.unit
  };
  return withOrientation(sized, landscape);
}

export const toPageUnit = (units: number, unit: PageUnit) => units / (unit === 'in' ? UNITS_PER_INCH : UNITS_PER_MM);
export const fromPageUnit = (value: number, unit: PageUnit) => value * (unit === 'in' ? UNITS_PER_INCH : UNITS_PER_MM);
